import React, { useState, useEffect, useRef } from 'react';
import { Form, InputGroup, Button, Dropdown, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import searchService from '../services/searchService';

const SearchBar = ({ className = '', placeholder = "Search surveys, blogs..." }) => {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const searchRef = useRef(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    // Close suggestions when clicking outside
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);

    // Clear previous timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Debounce suggestions
    if (value.trim().length >= 2) {
      timeoutRef.current = setTimeout(() => {
        loadSuggestions(value);
      }, 300);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const loadSuggestions = async (searchQuery) => {
    try {
      setLoading(true);
      const response = await searchService.getSuggestions(searchQuery);
      setSuggestions(response.data);
      setShowSuggestions(response.data.length > 0);
    } catch (error) {
      console.error('Error loading suggestions:', error);
      setSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (searchQuery = query) => {
    if (searchQuery.trim()) {
      setShowSuggestions(false);
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setQuery(suggestion);
    handleSearch(suggestion);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  return (
    <div className={`search-bar ${className}`} ref={searchRef}>
      <InputGroup>
        <Form.Control
          type="text"
          placeholder={placeholder}
          value={query}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
          onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
        />
        <Button variant="outline-secondary" onClick={() => handleSearch()}>
          {loading ? (
            <Spinner animation="border" size="sm" />
          ) : (
            <i className="fas fa-search"></i>
          )}
        </Button>
      </InputGroup>

      {showSuggestions && suggestions.length > 0 && (
        <div className="search-suggestions">
          <Dropdown.Menu show className="w-100 mt-1">
            {suggestions.map((suggestion, index) => (
              <Dropdown.Item
                key={index}
                onClick={() => handleSuggestionClick(suggestion)}
              >
                <i className="fas fa-search me-2 text-muted"></i>
                {suggestion}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </div>
      )}

      <style jsx>{`
        .search-bar {
          position: relative;
        }
        
        .search-suggestions {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          z-index: 1050;
        }
        
        .search-suggestions .dropdown-menu {
          border-top: none;
          border-top-left-radius: 0;
          border-top-right-radius: 0;
          box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
        }
        
        .search-suggestions .dropdown-item {
          padding: 0.5rem 1rem;
          cursor: pointer;
        }
        
        .search-suggestions .dropdown-item:hover {
          background-color: #f8f9fa;
        }
      `}</style>
    </div>
  );
};

export default SearchBar;